import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Button,
  Tag,
  Modal,
  Input,
  Space,
  Alert,
  message,
  Card,
} from "antd";
import {
  PlayCircleOutlined,
  CheckCircleOutlined,
  FastForwardOutlined,
  CloseCircleOutlined,
  MessageOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { formatLimpiar } from "../../../utils/formatLimpiar";
import activitiesService from "../../../services/activitiesService";
import { useUser } from "../../../context/UserContext";
import PageHeader from "../../../components/headers/PageHeader";
import { getActivityStateConfig } from "./activityStates";

// Función para obtener el comentario más reciente
const obtenerDatoMasReciente = (arrayDeObjetos) => {
  if (!arrayDeObjetos || arrayDeObjetos.length === 0) {
    return { comment: "Sin comentarios" };
  }

  const arrayOrdenado = arrayDeObjetos
    .slice()
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  return arrayOrdenado[0];
};

const { TextArea } = Input;

const SubActivityManager_v2 = ({
  Activities,
  consultaACTIVIDADES,
  onActivityUpdate,
  fetchData,
}) => {
  const user = useUser();
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [currentAction, setCurrentAction] = useState("");
  const [comment, setComment] = useState("");
  const [loadingStates, setLoadingStates] = useState({});
  const [activityState, setActivityState] = useState(null);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [localActivities, setLocalActivities] = useState(Activities);

  // Sincronizar actividades locales con las props y ordenarlas de menor a mayor
  useEffect(() => {
    if (Activities && Activities.length > 0) {
      const sortedActivities = [...Activities].sort(
        (a, b) => a.order - b.order
      );
      setLocalActivities(sortedActivities);
    } else {
      setLocalActivities(Activities);
    }
  }, [Activities]);

  // Función para manejar el estado de carga por botón
  const setButtonLoading = (activityId, action, isLoading) => {
    setLoadingStates((prev) => ({
      ...prev,
      [`${activityId}-${action}`]: isLoading,
    }));
  };

  // Función para obtener el estado de carga de un botón específico
  const getButtonLoading = (activityId, action) => {
    return loadingStates[`${activityId}-${action}`] || false;
  };

  // Función para limpiar todos los estados de carga
  const clearAllLoadingStates = () => {
    setLoadingStates({});
  };

  // Función para actualizar el estado local de una actividad
  const updateLocalActivityState = (
    activityId,
    newState,
    comment = "",
    falseAlarm = false
  ) => {
    setLocalActivities((prevActivities) => {
      const updatedActivities = prevActivities.map((activity) =>
        activity.protocol_activity_subactivity === activityId
          ? {
              ...activity,
              state: newState,
              ...(comment && {
                comments: [
                  ...(activity.comments || []),
                  {
                    id: Date.now(),
                    comment: comment,
                    date: new Date().toISOString(),
                    user: user?.name || "Usuario",
                  },
                ],
              }),
              ...(falseAlarm !== false && { falseAlarm }),
            }
          : activity
      );

      // Mantener el ordenamiento de menor a mayor después de la actualización
      return updatedActivities.sort((a, b) => a.order - b.order);
    });
  };

  // Generar una key única para la tabla basada en el estado de las actividades
  const tableKey = useMemo(() => {
    if (!Activities || Activities.length === 0) return "default";

    const states = Activities.map((activity) => activity.state).join("-");
    const orders = Activities.map((activity) => activity.order).join("-");
    const comments = Activities.map((activity) =>
      activity.comments ? activity.comments.length : 0
    ).join("-");
    const falseAlarms = Activities.map((activity) =>
      activity.falseAlarm ? "1" : "0"
    ).join("-");

    return `sub-activities-v2-${
      Activities.length
    }-${states}-${orders}-${comments}-${falseAlarms}-${Date.now()}`;
  }, [Activities]);

  const getButtonColor = (action) => {
    const colors = {
      start: "#1890ff",
      complete: "#52c41a",
      skip: "#d9d9d9",
      terminate: "#52c41a",
    };
    return colors[action];
  };

  const Iniciar = async (registro, action) => {
    const activityId = registro.protocol_activity_subactivity;
    setButtonLoading(activityId, action, true);

    try {
      const nuevoRegistro = {
        eventId: registro.eventId,
        protocol_activity_subactivity: registro.protocol_activity_subactivity,
        state: "Started",
        order: registro.order,
      };
      await executeAction(nuevoRegistro, action);
    } finally {
      // No necesitamos limpiar el estado de carga aquí porque executeAction ya lo hace
    }
  };

  const handleActionWithComment = async (registro, action) => {
    if (registro.isCommentMandatory) {
      setCurrentAction(action);
      setActivityState(registro);
      setShowCommentModal(true);
    } else {
      const activityId = registro.protocol_activity_subactivity;
      setButtonLoading(activityId, action, true);

      try {
        await executeAction(registro, action);
      } finally {
        // No necesitamos limpiar el estado de carga aquí porque executeAction ya lo hace
      }
    }
  };

  const executeAction = async (registro, action, commentText = "") => {
    let newState = "";
    let falseAlarm = false;

    switch (action) {
      case "start":
        newState = "Started";
        break;
      case "complete":
        newState = "Completed";
        break;
      case "terminate":
        newState = "Completed";
        break;
      case "false-alarm-yes":
        falseAlarm = true;
        newState = "Completed";
        break;
      case "false-alarm-no":
        newState = registro.protocol_activity_subactivity?.includes(
          "cerrar_evento"
        )
          ? "Terminated"
          : "Completed";
        break;
      case "skip":
        newState = "Skipped";
        break;
      default:
        return;
    }

    const nuevoRegistro = {
      ...registro,
      state: newState,
      comment: commentText,
      falseAlarm,
    };

    try {
      const response = await activitiesService.funcionActividad(
        nuevoRegistro,
        user?.idToken || ""
      );

      if (response) {
        message.success("Actividad actualizada correctamente");

        setShowCommentModal(false);
        setComment("");
        setCurrentAction("");

        // Limpiar todos los estados de carga para evitar bloqueos
        clearAllLoadingStates();

        // Actualizar estado local DESPUÉS de que la API responda exitosamente
        updateLocalActivityState(
          registro.protocol_activity_subactivity,
          newState,
          commentText,
          falseAlarm
        );

        if (onActivityUpdate) {
          onActivityUpdate(registro, newState);
        }

        // Solo recargar datos para casos específicos que requieren actualización del servidor
        if (falseAlarm && fetchData) {
          setTimeout(() => {
            fetchData();
          }, 100);
        }

        const isCloseEvent =
          registro.protocol_activity_subactivity?.includes("cerrar_evento");
        if (
          isCloseEvent &&
          (action === "terminate" || action === "complete") &&
          fetchData
        ) {
          setTimeout(() => {
            fetchData();
            if (consultaACTIVIDADES) {
              consultaACTIVIDADES();
            }
          }, 100);
        }

        // NO avanzar automáticamente al siguiente paso - el usuario debe hacerlo manualmente
        // Esto permite crear la siguiente sub-actividad cuando el usuario lo desee
      }
    } catch (error) {
      console.error("Error al ejecutar acción:", error);
      message.error(
        "No se logró realizar la acción. Puedes intentar nuevamente."
      );
      // Limpiar estados de carga en caso de error
      clearAllLoadingStates();
    }
  };

  const confirmAction = async () => {
    if (activityState.isCommentMandatory && !comment.trim()) {
      Modal.error({ title: "Error", content: "El comentario es obligatorio" });
      return;
    }

    const activityId = activityState.protocol_activity_subactivity;
    setButtonLoading(activityId, currentAction, true);

    try {
      await executeAction(activityState, currentAction, comment);
    } finally {
      // No necesitamos limpiar el estado de carga aquí porque executeAction ya lo hace
      setComment("");
      setCurrentAction("");
    }
  };

  const cancelAction = () => {
    setShowCommentModal(false);
    setComment("");
    setCurrentAction("");
    setActivityState(null);
  };

  // Función para iniciar edición de comentario
  const startEditComment = (commentId, currentText) => {
    // Solo permitir editar un comentario a la vez
    if (editingCommentId && editingCommentId !== commentId) {
      message.warning(
        "Termina de editar el comentario actual antes de editar otro"
      );
      return;
    }
    setEditingCommentId(commentId);
    setEditingCommentText(currentText);
  };

  // Función para guardar comentario editado
  const saveEditedComment = async (commentId, record) => {
    if (!editingCommentText.trim()) {
      message.error("El comentario no puede estar vacío");
      return;
    }

    setEditLoading(true);

    let registro = {
      comment: editingCommentText.trim(),
    };

    try {
      // Llamar al servicio para ejecutar la acción sobre la actividad
      await activitiesService.editarComentario(
        registro,
        user?.idToken ?? "",
        record.eventId,
        record.protocol_activity_subactivity
      );

      // Mostrar mensaje de éxito
      message.success("Comentario editado correctamente");

      // Actualizar el estado local del comentario
      setLocalActivities((prevActivities) => {
        const updatedActivities = prevActivities.map((activity) => {
          if (
            activity.protocol_activity_subactivity ===
            record.protocol_activity_subactivity
          ) {
            const updatedComments =
              activity.comments?.map((comment) =>
                comment.id === commentId
                  ? {
                      ...comment,
                      text: editingCommentText.trim(),
                      editedAt: new Date().toISOString(),
                    }
                  : comment
              ) || [];

            return {
              ...activity,
              comments: updatedComments,
            };
          }
          return activity;
        });

        // Mantener el ordenamiento de menor a mayor después de la actualización
        return updatedActivities.sort((a, b) => a.order - b.order);
      });

      if (onActivityUpdate) {
        onActivityUpdate(record, record.state);
      }

      setEditingCommentId(null);
      setEditingCommentText("");
    } catch (error) {
      console.error("Error al editar comentario:", error);
      message.error("No se pudo editar el comentario. Inténtalo nuevamente.");
    } finally {
      setEditLoading(false);
    }
  };

  // Función para cancelar edición de comentario
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };

  const columns = [
    {
      title: "Paso",
      dataIndex: "order",
      key: "order",
      render: (text, record) => <strong>{`Paso ${record.order}`}</strong>,
    },
    {
      title: "Sub Actividades",
      dataIndex: "description",
      key: "description",
      render: (_, record) =>
        formatLimpiar(
          record.protocol_activity_subactivity.split("_")[2] || ""
        ) || "Sin descripción disponible",
    },
    {
      title: "Último Comentario",
      dataIndex: "comments",
      key: "comments",
      render: (_, record) => {
        const ultimoComentario = obtenerDatoMasReciente(record.comments || []);

        return (
          <div style={{ maxWidth: 300 }}>
            <div style={{ fontWeight: "bold", marginBottom: 4 }}>
              {ultimoComentario.comment}
            </div>
            {ultimoComentario.editedAt ? (
              <div style={{ fontSize: "12px", color: "#1890ff" }}>
                Editado: {new Date(ultimoComentario.editedAt).toLocaleString()}
              </div>
            ) : (
              ultimoComentario.date && (
                <div style={{ fontSize: "12px", color: "#666" }}>
                  {new Date(ultimoComentario.date).toLocaleString()}
                </div>
              )
            )}
          </div>
        );
      },
    },
    {
      title: "Estado",
      dataIndex: "state",
      key: "state",
      render: (_, record) => {
        const config = getActivityStateConfig(record.state);
        return <Tag color={config.color}>{config.label}</Tag>;
      },
    },
    {
      title: "Editar Comentario",
      key: "editComment",
      width: 120,
      render: (_, record) => {
        const ultimoComentario = obtenerDatoMasReciente(record.comments || []);

        if (!record.comments || record.comments.length === 0) {
          return <span style={{ color: "#ccc" }}>Sin comentarios</span>;
        }

        // Si hay una edición en curso en otra fila, mostrar botón deshabilitado
        if (editingCommentId && editingCommentId !== ultimoComentario.id) {
          return (
            <Button
              type="default"
              size="small"
              icon={<MessageOutlined />}
              disabled
              style={{ width: "100%" }}
              title="Hay una edición en curso en otra fila"
            >
              Editar
            </Button>
          );
        }

        if (editingCommentId === ultimoComentario.id) {
          return (
            <Space direction="vertical" size="small" style={{ width: "100%" }}>
              <TextArea
                value={editingCommentText}
                onChange={(e) => setEditingCommentText(e.target.value)}
                rows={2}
                style={{ marginBottom: "8px", width: "100%" }}
                showCount
                maxLength={200}
                placeholder="Escribe tu comentario aquí..."
              />
              <Space size="small" style={{ justifyContent: "center" }}>
                <Button
                  type="primary"
                  size="small"
                  onClick={() => saveEditedComment(ultimoComentario.id, record)}
                  loading={editLoading}
                >
                  ✓
                </Button>
                <Button size="small" onClick={cancelEditComment}>
                  ✕
                </Button>
              </Space>
            </Space>
          );
        }

        return (
          <Button
            type="primary"
            size="small"
            icon={<MessageOutlined />}
            onClick={() =>
              startEditComment(ultimoComentario.id, ultimoComentario.comment)
            }
            title="Editar comentario"
            style={{ width: "100%" }}
          >
            Editar
          </Button>
        );
      },
    },
    {
      title: "Acciones",
      key: "actions",
      align: "center",
      render: (_, record) => {
        const isFalseAlarm =
          record.protocol_activity_subactivity?.includes("falsa-alarma");
        const isCloseEvent =
          record.protocol_activity_subactivity?.includes("cerrar_evento");

        switch (record.state) {
          case "Created":
            return (
              <Button
                type="primary"
                icon={<PlayCircleOutlined />}
                size="small"
                loading={getButtonLoading(
                  record.protocol_activity_subactivity,
                  "start"
                )}
                onClick={() => Iniciar(record, "start")}
                style={{ backgroundColor: getButtonColor("start") }}
              >
                Iniciar
              </Button>
            );
          case "Started":
            return (
              <Space wrap>
                {isFalseAlarm ? (
                  <>
                    <Button
                      type="primary"
                      icon={<CheckCircleOutlined />}
                      size="small"
                      loading={getButtonLoading(
                        record.protocol_activity_subactivity,
                        "false-alarm-no"
                      )}
                      onClick={() =>
                        handleActionWithComment(record, "false-alarm-no")
                      }
                    >
                      No, continuar
                    </Button>
                    <Button
                      danger
                      icon={<CloseCircleOutlined />}
                      size="small"
                      loading={getButtonLoading(
                        record.protocol_activity_subactivity,
                        "false-alarm-yes"
                      )}
                      onClick={() =>
                        handleActionWithComment(record, "false-alarm-yes")
                      }
                    >
                      Sí, falsa alarma
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      icon={
                        isCloseEvent ? (
                          <StopOutlined />
                        ) : (
                          <CheckCircleOutlined />
                        )
                      }
                      size="small"
                      loading={getButtonLoading(
                        record.protocol_activity_subactivity,
                        isCloseEvent ? "terminate" : "complete"
                      )}
                      onClick={() =>
                        handleActionWithComment(
                          record,
                          isCloseEvent ? "terminate" : "complete"
                        )
                      }
                      style={{
                        backgroundColor: getButtonColor(
                          isCloseEvent ? "terminate" : "complete"
                        ),
                      }}
                    >
                      {isCloseEvent ? "Terminar" : "Completar"}
                    </Button>
                    {!record.isMandatory && (
                      <Button
                        icon={<FastForwardOutlined />}
                        size="small"
                        loading={getButtonLoading(
                          record.protocol_activity_subactivity,
                          "skip"
                        )}
                        onClick={() => handleActionWithComment(record, "skip")}
                      >
                        Saltar
                      </Button>
                    )}
                  </>
                )}
              </Space>
            );
          default:
            return null;
        }
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card
        title={
          <PageHeader
            title={
              "Paso " +
              Activities[0]?.order +
              " " +
              formatLimpiar(
                Activities[0]?.protocol_activity_subactivity.split("_")[1] || ""
              )
            }
          ></PageHeader>
        }
      >
        <Table
          columns={columns}
          dataSource={localActivities}
          rowKey={(record) => record.protocol_activity_subactivity}
          key={tableKey}
        />
      </Card>
      <Modal
        title={
          <Space>
            <MessageOutlined />
            {currentAction === "false-alarm-yes"
              ? "Justifica la falsa alarma"
              : activityState?.isCommentMandatory
              ? "Comentario obligatorio"
              : "Agregar comentario"}
          </Space>
        }
        open={showCommentModal}
        onOk={confirmAction}
        onCancel={cancelAction}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={getButtonLoading(
          activityState?.protocol_activity_subactivity,
          currentAction
        )}
        width={500}
      >
        {activityState?.isCommentMandatory && (
          <Alert
            message="Este comentario es obligatorio"
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
          />
        )}

        <TextArea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Escribe tu comentario aquí..."
          rows={4}
          showCount
        />
      </Modal>
    </div>
  );
};

export default SubActivityManager_v2;
